// Loads the Telegram Mini App SDK (`telegram-web-app.js`) only when the page was opened by a Telegram client, so plain
// browser visitors of the website never contact telegram.org. `index.html` used to load it for everybody.
// Telegram clients append `#tgWebAppData=…&tgWebAppVersion=…&tgWebAppPlatform=…` to the URL, the SDK keeps those
// parameters in sessionStorage (`__telegram__initParams`) so they survive in-app navigation, and the native mobile
// webviews define `TelegramWebviewProxy`.

const SDK_URL = 'https://telegram.org/js/telegram-web-app.js'
const STORED_PARAMS_KEY = '__telegram__initParams'
const LOAD_TIMEOUT_MS = 5000

interface LaunchWindow {
  location: { hash: string }
  sessionStorage?: Pick<Storage, 'getItem'>
  TelegramWebviewProxy?: unknown
  Telegram?: { WebApp?: unknown }
}

export function launchedFromTelegram(win: LaunchWindow = window as unknown as LaunchWindow): boolean {
  if (/[#&]tgWebApp(?:Data|Version)=/.test(win.location.hash)) return true
  if (win.TelegramWebviewProxy !== undefined) return true
  try {
    return win.sessionStorage?.getItem(STORED_PARAMS_KEY) != null
  } catch {
    return false
  }
}

let loading: Promise<void> | null = null

// Resolves once the SDK ran (or at once outside Telegram). A failed or slow load also resolves, after a warning: the app then
// behaves as in a plain browser instead of showing a blank page.
export function loadTelegramSdk(): Promise<void> {
  if (loading) return loading
  if (!launchedFromTelegram() || (window as unknown as LaunchWindow).Telegram?.WebApp) {
    loading = Promise.resolve()
    return loading
  }
  loading = new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      console.warn('telegram-web-app.js did not load in time')
      resolve()
    }, LOAD_TIMEOUT_MS)
    const script = document.createElement('script')
    script.src = SDK_URL
    script.onload = () => {
      clearTimeout(timer)
      resolve()
    }
    script.onerror = () => {
      clearTimeout(timer)
      console.warn('telegram-web-app.js failed to load')
      resolve()
    }
    document.head.appendChild(script)
  })
  return loading
}
